/**
 * TiposNovedadPage - Página de catálogo de Tipos de Novedad
 * Muestra los tipos de novedad disponibles en el sistema (valores ENUM)
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import {
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { PageHeader, LoadingSpinner } from '../components/common';
import { useNotification } from '../context/NotificationContext';
import api from '../services/api';

/**
 * Tipos de novedad definidos en el sistema (ENUM del modelo Novedad)
 */
const TIPOS_NOVEDAD = [
  {
    id: 'cambio_fecha',
    nombre: 'Cambio de Fecha',
    descripcion: 'Solicitud de cambio de fecha de pago del cheque',
    icon: CalendarIcon,
    color: 'info',
  },
  {
    id: 'rechazo_bancario',
    nombre: 'Rechazo Bancario',
    descripcion: 'Cheque rechazado por el banco',
    icon: ErrorIcon,
    color: 'error',
  },
  {
    id: 'fondos_insuficientes',
    nombre: 'Fondos Insuficientes',
    descripcion: 'Cuenta sin fondos suficientes para el pago',
    icon: WarningIcon,
    color: 'warning',
  },
  {
    id: 'error_datos',
    nombre: 'Error en Datos',
    descripcion: 'Error en los datos del cheque o cuenta',
    icon: InfoIcon,
    color: 'default',
  },
  {
    id: 'solicitud_cliente',
    nombre: 'Solicitud del Cliente',
    descripcion: 'Novedad solicitada por el cliente',
    icon: DescriptionIcon,
    color: 'primary',
  },
  {
    id: 'otro',
    nombre: 'Otro',
    descripcion: 'Otros tipos de novedades',
    icon: HelpIcon,
    color: 'default',
  },
];

const TiposNovedadPage = () => {
  const { showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [estadisticas, setEstadisticas] = useState({});

  // Cargar estadísticas de novedades por tipo
  const loadEstadisticas = async () => {
    try {
      setLoading(true);
      const response = await api.get('/novedades/estadisticas-por-tipo');
      const data = response.data?.data || response.data || {};
      setEstadisticas(data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
      // No mostrar error si el endpoint no existe, solo log
      if (error.response?.status !== 404) {
        showError('No se pudieron cargar las estadísticas');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEstadisticas();
  }, []);

  if (loading) {
    return <LoadingSpinner message="Cargando tipos de novedad..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Tipos de Novedad"
        subtitle="Catálogo de tipos de novedades disponibles en el sistema"
        breadcrumbs={[
          { label: 'Inicio', path: '/dashboard' },
          { label: 'Catálogos' },
          { label: 'Tipos de Novedad' },
        ]}
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Estos son los tipos de novedad predefinidos en el sistema. Se utilizan para
          clasificar las incidencias relacionadas con los cheques.
        </Typography>
      </Alert>

      {/* Tabla de Tipos de Novedad */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50"></TableCell>
                <TableCell>
                  <strong>Tipo</strong>
                </TableCell>
                <TableCell>
                  <strong>Descripción</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Código</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Cantidad</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {TIPOS_NOVEDAD.map((tipo) => {
                const IconComponent = tipo.icon;
                const cantidad = estadisticas[tipo.id] || 0;

                return (
                  <TableRow key={tipo.id} hover>
                    <TableCell>
                      <IconComponent color={tipo.color} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight={500}>
                        {tipo.nombre}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {tipo.descripcion}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={tipo.id}
                        size="small"
                        variant="outlined"
                        color={tipo.color}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={cantidad}
                        size="small"
                        color={cantidad > 0 ? 'primary' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Información adicional */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Información
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Los tipos de novedad están definidos en el modelo de base de datos como valores
            ENUM. Para agregar, modificar o eliminar tipos de novedad, se requiere una
            actualización del esquema de base de datos.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Nota:</strong> Este catálogo es de solo lectura. Los tipos se utilizan
            al registrar novedades en los cheques desde el módulo de Novedades.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TiposNovedadPage;
